import { User, Key, Webhook, Shield, Database, RefreshCw, ExternalLink } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';

interface SettingsUser {
  id: string;
  username: string;
  email: string | null;
  avatarUrl: string | null;
  isAdmin: boolean;
}

interface SettingsProps {
  user: SettingsUser;
}

function PayrollIntegration() {
  const [apiUrl, setApiUrl] = useState('');
  const [savedUrl, setSavedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [planCount, setPlanCount] = useState<number | null>(null);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch('/api/pricing-proxy/config');
      if (res.ok) {
        const data = await res.json();
        setApiUrl(data.payrollApiUrl || '');
        setSavedUrl(data.payrollApiUrl || '');
      }
    } catch {
      // Config endpoint not available yet
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const saveUrl = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/pricing-proxy/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: apiUrl }),
      });
      if (res.ok) {
        setSavedUrl(apiUrl);
        setTestResult({ ok: true, message: 'URL saved' });
      } else {
        const data = await res.json();
        setTestResult({ ok: false, message: data.error || 'Failed to save' });
      }
    } catch {
      setTestResult({ ok: false, message: 'Network error' });
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch('/api/pricing-proxy');
      if (res.ok) {
        const data = await res.json();
        setPlanCount(data.plans?.length ?? 0);
        setTestResult({ ok: true, message: `Connected — ${data.plans?.length ?? 0} plans found` });
      } else {
        const data = await res.json();
        setTestResult({ ok: false, message: data.error || `HTTP ${res.status}` });
      }
    } catch {
      setTestResult({ ok: false, message: 'Connection failed' });
    } finally {
      setTesting(false);
    }
  };

  const refreshCache = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/pricing-proxy/refresh', { method: 'POST' });
      if (res.ok) {
        setTestResult({ ok: true, message: 'Cache cleared — next request will re-fetch' });
      }
    } catch {
      setTestResult({ ok: false, message: 'Failed to clear cache' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-800 rounded-xl p-6">
      <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <Database className="w-5 h-5" />
        Payroll Integration
      </h2>
      <p className="text-gray-400 mb-4">
        Configure the connection to the payroll API for live pricing data on payrovia.com.
      </p>

      {/* API URL Input */}
      <div className="space-y-3 mb-6">
        <label className="block text-sm font-medium text-gray-300">Payroll API URL</label>
        <div className="flex gap-3">
          <input
            type="url"
            value={apiUrl}
            onChange={(e) => setApiUrl(e.target.value)}
            placeholder="https://payrovia.app/api"
            className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary-500"
          />
          <button
            onClick={saveUrl}
            disabled={loading || apiUrl === savedUrl}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          >
            Save
          </button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 mb-4">
        <button
          onClick={testConnection}
          disabled={testing || !savedUrl}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          {testing ? 'Testing...' : 'Test Connection'}
        </button>
        <button
          onClick={refreshCache}
          disabled={loading || !savedUrl}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh Cache
        </button>
      </div>

      {/* Test Result */}
      {testResult && (
        <div className={`p-3 rounded-lg text-sm ${testResult.ok ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'}`}>
          {testResult.message}
        </div>
      )}

      {/* Current Pricing Preview */}
      {planCount !== null && testResult?.ok && (
        <div className="mt-4 p-3 bg-gray-700/50 rounded-lg">
          <p className="text-sm text-gray-300">{planCount} pricing plan{planCount !== 1 ? 's' : ''} loaded from payroll API</p>
        </div>
      )}
    </div>
  );
}

export default function Settings({ user }: SettingsProps) {
  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-8">Settings</h1>

      <div className="space-y-8">
        {/* Profile Section */}
        <div className="bg-gray-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <User className="w-5 h-5" />
            Profile
          </h2>
          <div className="flex items-center gap-4">
            <img
              src={user.avatarUrl || `https://github.com/${user.username}.png`}
              alt={user.username}
              className="w-16 h-16 rounded-full"
            />
            <div>
              <p className="text-lg font-medium text-white">{user.username}</p>
              <p className="text-gray-400">{user.email || 'No email set'}</p>
              {user.isAdmin && (
                <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-primary-500/10 text-primary-400 text-xs rounded-full">
                  <Shield className="w-3 h-3" />
                  Admin
                </span>
              )}
            </div>
          </div>
        </div>

        {/* GitHub Connection */}
        <div className="bg-gray-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Key className="w-5 h-5" />
            GitHub Connection
          </h2>
          <p className="text-gray-400 mb-4">
            Your account is connected to GitHub. This allows SiteHub to access your
            repositories and set up webhooks for auto-deployment.
          </p>
          <div className="flex items-center gap-3 p-4 bg-gray-700/50 rounded-lg">
            <img
              src={`https://github.com/${user.username}.png`}
              alt="GitHub"
              className="w-10 h-10 rounded-full"
            />
            <div>
              <p className="text-white font-medium">@{user.username}</p>
              <p className="text-sm text-gray-500">Connected via GitHub OAuth</p>
            </div>
          </div>
        </div>

        {/* Webhook Configuration */}
        <div className="bg-gray-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Webhook className="w-5 h-5" />
            Webhook Endpoint
          </h2>
          <p className="text-gray-400 mb-4">
            Configure this URL in your GitHub repositories to enable auto-deployment:
          </p>
          <div className="bg-gray-900 p-4 rounded-lg font-mono text-sm text-gray-300 break-all">
            {window.location.origin}/webhooks/github
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Webhooks are automatically configured when you connect a repository to a site.
          </p>
        </div>

        {/* Payroll Integration */}
        <PayrollIntegration />

        {/* Platform Info */}
        <div className="bg-gray-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">
            Platform Information
          </h2>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-gray-500">Version</dt>
              <dd className="text-white">1.0.0</dd>
            </div>
            <div>
              <dt className="text-gray-500">Environment</dt>
              <dd className="text-white">{import.meta.env.MODE}</dd>
            </div>
            <div>
              <dt className="text-gray-500">API Endpoint</dt>
              <dd className="text-white font-mono">{window.location.origin}/api</dd>
            </div>
            <div>
              <dt className="text-gray-500">Documentation</dt>
              <dd>
                <a
                  href="/docs/agent-guide"
                  className="text-primary-400 hover:text-primary-300"
                >
                  Agent Guide
                </a>
              </dd>
            </div>
          </dl>
        </div>

        {/* Danger Zone */}
        <div className="bg-gray-800 rounded-xl p-6 border border-red-500/20">
          <h2 className="text-lg font-semibold text-red-400 mb-4">
            Danger Zone
          </h2>
          <p className="text-gray-400 mb-4">
            These actions are irreversible. Proceed with caution.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => {
                if (confirm('Are you sure you want to log out?')) {
                  window.location.href = '/auth/logout';
                }
              }}
              className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-left"
            >
              Log out of all sessions
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
